import React, { useState, useEffect } from "react";
import { Modal, Button, TextField, Box, FormControlLabel, Checkbox } from "@mui/material";
import AuthService from "services/auth-service";
import Autocomplete from "@mui/material/Autocomplete";
import moment from "moment";

const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 400,
    bgcolor: "background.paper",
    borderRadius: 2,
    boxShadow: 24,
    p: 4,
};

const TransactionSaveModal = ({ open, handleClose, transaction, selectedAccount, total }) => {
    const [categories, setCategories] = useState([]);
    const [formData, setFormData] = useState({
        amount: "",
        date: "",
        time: "",
        account_id: "",
        category: "",
        note: "",
        by_total: false,
    });

    useEffect(() => {
        fetchCategories();
    }, []);

    useEffect(() => {
        setFormData({
            amount: transaction ? formatAmount(transaction.amount / 100) : "0.00",
            date: transaction ? moment(transaction.date).format("YYYY-MM-DD") : moment().format("YYYY-MM-DD"),
            time: transaction ? moment(transaction.date).format("HH:mm") : moment().format("HH:mm"),
            account_id: selectedAccount ? selectedAccount.id : "",
            category: transaction ? transaction.category : "",
            note: transaction ? transaction.note : "",
            by_total: false,
        });
    }, [transaction]);

    useEffect(() => {
        if (selectedAccount) {
            setFormData({
                ...formData,
                account_id: selectedAccount.id || "",
            });
        }
    }, [selectedAccount]);

    useEffect(() => {
        if (open && !transaction) {
            handleCategoryChange(null, categories.find((option) => option.value === "food"));
        }
    }, [open]);

    const fetchCategories = async () => {
        const response = await AuthService.getEnums("categories");
        setCategories(
            Object.entries(response).map(([key, value]) => ({
                label: key,
                value: value,
            }))
        );
    };

    const handleCategoryChange = (event, newValue) => {
        setFormData({ ...formData, category: newValue ? newValue.value : "" });
    };

    const handleAccountChange = (event, newValue) => {
        setFormData({ ...formData, account_id: newValue ? newValue.id : "" });
    };

    const handleInputChange = (e) => {
        const { name, type, checked, value } = e.target;
        if (type === "checkbox") {
            setFormData((prev) => ({ ...prev, [name]: checked }));
        } else if (name === "amount") {
            const cleanedValue = value.toString().replace(/[^0-9.-]+/g, "");
            setFormData({ ...formData, [name]: cleanedValue });
        } else {
            setFormData({ ...formData, [name]: value });
        }
    };

    const formatAmount = (value) => {
        const cleanedValue = value.toString().replace(/[^0-9.-]+/g, "");
        if (isNaN(cleanedValue) || cleanedValue === "") return "";
        return parseFloat(cleanedValue).toLocaleString("en-US", {
            style: "decimal",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
    };

    const handleBlur = () => {
        setFormData((prev) => ({
            ...prev,
            amount: formatAmount(prev.amount),
        }));
    };

    const handleSubmit = async () => {
        let amount = (formData.amount.replace(/[^0-9.-]+/g, "") * 100);
        if (formData.by_total) {
            amount -= total;
        }
        const transactionData = {
            category: formData.category,
            account_id: formData.account_id,
            amount: amount,
            date: moment(`${formData.date} ${formData.time}:01`, "YYYY-MM-DD HH:mm:ss").format("YYYY-MM-DD HH:mm:ss"),
            note: formData.note,
        };

        transaction ? await AuthService.updateTransaction(transaction.id, transactionData) : await AuthService.createTransaction(transactionData);
        handleClose();
    };

    const handleDelete = async () => {
        await AuthService.deleteTransaction(transaction.id);
        handleClose();
    };

    return (
        <Modal open={open} onClose={handleClose}>
            <Box sx={modalStyle}>
                <h2>{transaction ? "Edit Transaction" : "Create Transaction"}</h2>
                
                {!transaction ? (
                    <Box display="flex" alignItems="center" gap={2}>
                        <TextField
                            label={formData.by_total ? "Force Total" : "Amount"}
                            name="amount"
                            value={formData.amount}
                            onChange={handleInputChange}
                            onBlur={handleBlur}
                            sx={{ flex: 1 }}
                            margin="normal"
                            type="text"
                        />
                        <Checkbox
                            checked={formData.by_total}
                            onChange={handleInputChange}
                            name="by_total"
                        />
                    </Box>
                ) : (
                    <TextField
                        label="Amount"
                        name="amount"
                        value={formData.amount}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        fullWidth
                        margin="normal"
                        type="text"
                    />
                )}

                <TextField
                    label="Date"
                    name="date"
                    type="date"
                    value={formData.date}
                    onChange={handleInputChange}
                    fullWidth
                    margin="normal"
                    InputLabelProps={{ shrink: true }}
                />
                
                <TextField
                    label="Time"
                    name="time"
                    type="time"
                    value={formData.time}
                    onChange={handleInputChange}
                    fullWidth
                    margin="normal"
                    InputLabelProps={{ shrink: true }}
                />
                
                <Autocomplete
                    options={[selectedAccount]}
                    getOptionLabel={(option) => option.name}
                    value={selectedAccount || null}
                    onChange={handleAccountChange}
                    fullWidth
                    margin="normal"
                    sx={{ mb: 2, mt: 2 }}
                    renderInput={(params) => (
                        <TextField {...params} label="Select Account" variant="outlined" />
                    )}
                />

                <Autocomplete
                    options={categories}
                    getOptionLabel={(option) => option.label}
                    value={categories.find((option) => option.value === formData.category) || null}
                    onChange={handleCategoryChange}
                    fullWidth
                    margin="normal"
                    sx={{ mb: 1, mt: 3 }}
                    renderInput={(params) => (
                        <TextField {...params} label="Select Category" variant="outlined" />
                    )}
                />

                <TextField
                    label="Note"
                    name="note"
                    onChange={handleInputChange}
                    value={formData.note}
                    fullWidth
                    margin="normal"
                    type="text"
                    InputLabelProps={{ shrink: true }}
                />

                <Box display="flex" justifyContent="flex-end" mt={2}>
                    <Button onClick={handleClose} sx={{ mr: 2 }}>
                        Cancel
                    </Button>
                    {transaction && (
                        <Button onClick={handleDelete} sx={{ mr: 2, backgroundColor: "red" }} variant="gradient" color="error">
                            Delete
                        </Button>
                    )}
                    <Button onClick={handleSubmit} variant="contained" color="info">
                        {transaction ? "Update" : "Create"}
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
};

export default TransactionSaveModal;
